<template>
  <SectionHeader
    :show-content="customScoringSettingsShowContents"
    :widget-title="$t('HEADER_CUSTOM_SCORING_SETTINGS')"
    @onShowContentsClick="onCustomScoringSettingsShowContentsClick()"
  >
    <template #content>
      <div
        class="form-horizontal"
      >
        <Field
          :label="$t('LABEL_CUSTOM_SCORING_TOGGLE')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('LABEL_CUSTOM_SCORING_TOGGLE_TT')"
        >
          <template #element>
            <div v-if="hasCustomScoringV1" class="col-xs-12 col-sm-9 col-md-8 col-lg-6">
              <div v-html="$t('MESSAGE_CUSTOM_SCORING_V1')" class="bs-callout bs-callout-sm bs-callout-warning m-t-5" />
            </div>
            <div v-else class="col-sm-6 col-md-6">
              <ValidationProvider slim>
                <MultipleToggle
                  :value="customScoring.state"
                  :options="customScoringSettingOptions"
                  @onToggle="customScoringSettingChange($event)"
                />
              </ValidationProvider>
            </div>
          </template>
        </Field>
        <template v-if="customScoring.state === 'On' || customScoring.state === 'Required'">
          <!-- URL Field Start -->
          <Field
            :label="$t('LABEL_CUSTOM_SCORING_URL')"
            :has-tooltip="true"
            :tooltip-text="$t('LABEL_CUSTOM_SCORING_URL_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="customScoringUrl"
                rules="required|url"
                slim
              >
                <div :class="classes">
                  <div class="col-xs-10 col-sm-6">
                    <HttpUrl
                      v-model="customScoring.scoreSettingsV2.url"
                      name="customScoringUrl"
                      @input="updateCustomScoringSettings"
                    />             
                  </div>
                  <label 
                    v-t="'REQUIRED'"
                    class="col-xs-2 col-sm-3 form-group__label-required"
                  />
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <!-- URL Field End -->

          <!-- HTTP Method Field Start -->
          <Field
            :label="$t('LABEL_CUSTOM_SCORING_METHOD')"
            :has-tooltip="true"
            :tooltip-text="$t('LABEL_CUSTOM_SCORING_METHOD_TT')"
          >
            <template #element>
              <div class="col-xs-10 col-sm-3 col-md-2">
                <HttpMethodSelector
                  v-model="customScoring.scoreSettingsV2.method"
                  name="customScoringConnectionMethod"
                  :custom-options="httpConnectionOptions"
                  @input="onCustomScoringConnectionMethodChange"
                />
              </div>
            </template>
          </Field>
          <!-- HTTP Method Field End -->

          <template v-if="showRequestBody">

            <!-- Body Field Start -->
            <Field
              :label="$t('LABEL_CUSTOM_SCORING_BODY')"
              :has-tooltip="true"
              :tooltip-text="$t('LABEL_CUSTOM_SCORING_BODY_TT')"
            >
              <template #element>
                <ValidationProvider
                  v-slot="{ classes }"
                  ref="customScoringBody"
                  rules="required"
                  slim
                >
                  <div :class="classes">
                    <div class="col-xs-10 col-sm-6">
                      <HttpBody
                        v-model="customScoring.scoreSettingsV2.body"
                        :has-macros="true"
                        body-type="json"
                        name="customScoringBody"
                        custom-class="no-radius"
                        @input="updateCustomScoringSettings"
                      />
                      <label
                        v-if="!isValidJson(customScoring.scoreSettingsV2.body)"
                        class="m-t-5 red"
                        v-t="'ERROR_CUSTOM_SCORING_JSON'"
                      />
                    </div>                   
                    <label 
                      v-t="'REQUIRED'"
                      class="col-xs-2 col-sm-3 form-group__label-required"
                    />
                  </div>
                </ValidationProvider>
              </template>
            </Field>
            <!-- Body Field End -->
          </template>

          <!--Headers Field Start -->
          <Field
            :label="$t('LABEL_CUSTOM_SCORING_HEADERS')"
            :has-tooltip="true"
            :tooltip-text="$t('LABEL_CUSTOM_SCORING_HEADERS_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="customScoringHeaders"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-8 col-md-6">
                    <HttpHeaders
                      v-model="customScoring.scoreSettingsV2.headers"
                      name="customScoringHeaders"
                      @input="updateCustomScoringSettings"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
          <!-- Headers Field End -->

          <div class="row m-t-20">
            <CustomScoringSampleTokens
              v-if="selectedMacros && selectedMacros.length"
              :selected-macros.sync="selectedMacros"
              class="col-sm-offset-3 col-sm-8 col-md-6 m-b-15"
            />
            <div v-if="showInitialMessage && !isRequestLoading" class="col-sm-offset-3 col-sm-8 col-md-6">
              <div class="bs-callout bs-callout-sm bs-callout-primary m-t-5">
                <p>{{ $t('MESSAGE_CUSTOM_SCORING_INITIAL') }}</p>
              </div>
            </div>
          </div>
          <div class="row m-b-15">
            <div class="col-sm-offset-3 col-sm-9">
              <button
                v-if="customScoringModified"
                class="btn btn-sm btn-danger m-r-10"
                type="button"
                @click="revertChanges"
              >
                {{ $t('REVERT') }}
              </button>
              <button
                class="btn"
                type="button"
                @click="runRequest"
                :disabled="!isRequestReady"
              >
                {{ $t('BUTTON_RUN_REQUEST') }}
                <i aria-hidden="true" class="fa fa-play" />
              </button>
              <Loader
                v-if="isRequestLoading"
                custom-class="size-sm m-l-5"
              />
              <i v-if="requestSuccess && !isRequestLoading" class="fa fa-check text-success m-l-10" aria-hidden="true" />
              <i v-if="requestFail && !isRequestLoading" class="fa fa-warning text-danger m-l-10" aria-hidden="true" />
            </div>
          </div>
          <div class="row">
            <div v-if="requestSuccess" class="col-sm-offset-3 col-sm-8 col-md-6">
              <div class="bs-callout bs-callout-sm bs-callout-success m-t-5">
                <p class="m-b-10">{{ $t('MESSAGE_CUSTOM_SCORING_SUCCESS') }}</p>
                <p class="m-b-10 text-code">{{ customScoring.scoreSettingsV2.method.toUpperCase() }} {{ completedCustomScoringRequestUrl }}</p>
                <div v-if="customScoring.scoreSettingsV2.method === 'post'">
                  <div class="m-b-10">{{ $t('LABEL_CUSTOM_SCORING_BODY') }}</div>
                  <div class="prism-editor-wrapper no-radius m-t-5 m-b-15 f-s-12">
                    <pre class="pre--basic">{{ customScoringRequestBody }}</pre>
                  </div>
                </div>
                <span
                  class="clickable text-underline m-b-10"
                  @click="toggleResponse"
                >
                  {{ $t('LABEL_VIEW_RESPONSE') }}
                  <i
                    class="fa fa-angle-right m-l-5"
                    :class="{'fa-rotate-90': showRequestResponse}"
                    aria-hidden="true"
                  />
                </span>
                <collapse v-model="showRequestResponse">
                  <div class="prism-editor-wrapper no-radius m-t-10 f-s-12">
                    <pre class="pre--basic">{{ customScoringRequestResponse }}</pre>
                  </div>
                </collapse>
              </div>
            </div>
            <div v-if="requestFail" class="col-sm-offset-3 col-sm-8 col-md-6">
              <div class="bs-callout bs-callout-sm bs-callout-danger m-t-5">
                <p>{{ $t('MESSAGE_CUSTOM_SCORING_FAILED') }}</p>
                <div
                  v-if="customScoringRequestError"
                  class="m-t-10"
                >
                  <span
                    class="clickable text-underline m-b-10"
                    @click="toggleError"
                  >
                    {{ $t('LABEL_VIEW_ERROR') }}
                    <i
                      class="fa fa-angle-right m-l-5"
                      :class="{'fa-rotate-90': showRequestError}"
                      aria-hidden="true"
                    />
                  </span>
                  <collapse v-model="showRequestError">
                    <div class="prism-editor-wrapper no-radius m-t-10 f-s-12">
                      <pre class="pre--basic">{{ customScoringRequestError }}</pre>
                    </div>
                  </collapse>
                </div>
              </div>
            </div>
          </div>
          <template v-if="showParsingSettings">
            <CustomScoringParsingSettings
              :acceptance-parsing.sync="acceptanceParsing"
              :selected-score-parsing.sync="selectedScoreParsing"
              :custom-tags-selected.sync="customTagsSelected"
              :selectable-properties-config="selectablePropertiesConfig"
              :custom-tags-config="customTagsConfig"
              @updateAcceptanceParsingSettings="updateCustomScoringSettings"
              @updateScoreParsingSettings="updateScoreParsingSettings"
              @addCustomTag="addCustomTag"
              @removeCustomTag="removeCustomTag"
            />
          </template>
        </template>
      </div>
    </template>
  </SectionHeader>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { STATUS_SUCCESS, STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import {
  extractProperties,
  sanitizeCustomTagsForRequest,
  sanitizeCustomTagsForDisplay,
  sanitizeAcceptanceParsingForRequest,
  sanitizeAcceptanceParsingForDisplay,
  sanitizeScoreParsingForRequest,
  sanitizeScoreParsingForDisplay
} from '../../helpers/customScoring';
import { buildMacros } from '@/core/components/HttpForm/helpers/index';
import { httpConnectionOptions } from '../../helpers/constants';
import CustomScoringSampleTokens from './CustomScoringSampleTokens';
import CustomScoringParsingSettings from './CustomScoringParsingSettings';
import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import HttpUrl from '@/core/components/HttpForm/HttpUrl';
import HttpMethodSelector from '@/core/components/HttpForm/HttpMethodSelector';
import HttpBody from '@/core/components/HttpForm/HttpBody';
import HttpHeaders from '@/core/components/HttpForm/HttpHeaders';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import Loader from '@/core/components/Loader';
import messages from '../locales';
import sharedMessages from '@/core/locales';

const defaultParsingStep = {
  property: '',
  operator: {label: 'Equals', value: '==='},
  value: ''
};

export default {
  name: 'CustomScoringSettings',
  components: {
    CustomScoringSampleTokens,
    CustomScoringParsingSettings,
    SectionHeader,
    Field,
    MultipleToggle,
    HttpUrl,
    HttpMethodSelector,
    HttpBody,
    HttpHeaders,
    SearchableDropdown,
    Loader
  }, 

  props: {
    currentCustomScoring: {
      type: Object,
      default: () => ({})
    },
    hasCustomScoringV1: {
      type: Boolean,
      default: false
    },
    customScoringModified: {
      type: Boolean,
      default: false
    },
    saveTriggered: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      customScoringSettingsShowContents: true,
      customScoring: cloneDeep(this.currentCustomScoring),
      customScoringSettingOptions: [
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DISABLED'),
          value: 'Off'
        },
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_ENABLED'),
          value: 'On'
        },
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_REQUIRED'),
          value: 'Required'
        }
      ],
      selectablePropertiesConfig: {
        options: [],
        placeholder: 'Choose Property',
      },
      customTagsConfig: {
        options: [],
        placeholder: 'Choose Tag',
      },
      isCustomScoringUrlValid: false,
      selectedMacros: [],
      customTagsSelected: [],
      completedCustomScoringRequestUrl: '',
      selectedScoreParsing: null,
      acceptanceParsing: [{...defaultParsingStep}],
      customScoringRequestResponse: null,
      customScoringRequestError: null,
      showRequestResponse: false,
      showRequestError: false,
      hasExistingAcceptanceParsing: false,
      hasExistingScoreParsing: false,
      hasExistingCustomTags: false,
      watchCounter: 0
    };
  },
  computed: {
    ...mapGetters('dynamicInboundCalls', [
      'customScoringRequestStatus'
    ]),
    showRequestBody() {
      return this.customScoring.scoreSettingsV2.method === 'post' || this.customScoring.scoreSettingsV2.method === 'put' || this.customScoring.scoreSettingsV2.method === 'patch';
    },
    showInitialMessage() {
      return this.isRequestReady && !this.requestSuccess && !this.requestFail;
    },
    showParsingSettings() {
      return (this.hasExistingAcceptanceParsing || this.requestSuccess) && !this.isRequestLoading;
    },
    isRequestLoading() {
      return this.customScoringRequestStatus.status == STATUS_PENDING;
    },
    requestSuccess() {
      return this.customScoringRequestStatus.status == STATUS_SUCCESS;
    },
    requestFail() {
      return this.customScoringRequestStatus.status == STATUS_ERROR;
    },
    isRequestReady() {
      if (this.showRequestBody) {
        return this.isCustomScoringUrlValid && this.customScoring.scoreSettingsV2.body && this.isSampleMacrosValid && this.isValidJson(this.customScoring.scoreSettingsV2.body) && (this.watchCounter > 1 || !this.hasExistingAcceptanceParsing);
      }
      return this.isCustomScoringUrlValid && this.isSampleMacrosValid && (this.watchCounter > 1 || !this.hasExistingAcceptanceParsing);
    },
    isSampleMacrosValid() {
      if (this.selectedMacros?.length) {
        return this.selectedMacros.every(macro => macro.hasOwnProperty('value') && macro['value']);
      }
      return true;
    },
    customScoringRequestUrl() {
      if (this.selectedMacros && this.selectedMacros.length) {
        let requestUrl = this.customScoring.scoreSettingsV2.url;
        this.selectedMacros.forEach(({ name, value }) => {
          requestUrl = requestUrl.replaceAll(name, value);
        });
        return requestUrl;
      }
      return this.customScoring.scoreSettingsV2.url;
    },
    customScoringRequestBody() {
      if (this.customScoring.scoreSettingsV2.body) {
        if (this.selectedMacros && this.selectedMacros.length) {
          let requestBody = this.customScoring.scoreSettingsV2.body;
          this.selectedMacros.forEach(({ name, value }) => {
            requestBody = requestBody.replaceAll(name, value);
          });
          if (this.isValidJson(requestBody)) {
            return JSON.parse(requestBody);
          }
          return requestBody;
        }
        if (this.isValidJson(this.customScoring.scoreSettingsV2.body)) {
          return JSON.parse(this.customScoring.scoreSettingsV2.body);
        }
        return this.customScoring.scoreSettingsV2.body;
      }
      return '';
    }
  },
  watch: {
    'customScoring.scoreSettingsV2.url': {
      immediate: false,
      async handler(newValue) {
        this.isCustomScoringUrlValid = await this.validateCustomScoringUrl();
        this.selectedMacros = buildMacros(newValue, this.customScoring.scoreSettingsV2.body, this.selectedMacros);
        this.watchCounter++;
      }
    },
    'customScoring.scoreSettingsV2.body': {
      immediate: false,
      async handler(newValue) {
        this.isCustomScoringUrlValid = await this.validateCustomScoringUrl();
        this.selectedMacros = buildMacros(this.customScoring.scoreSettingsV2.url, newValue, this.selectedMacros);
        if (this.watchCounter === 0 && this.hasExistingAcceptanceParsing) {
          this.selectedMacros = [];
        }
        this.watchCounter++;
      }
    },
    'saveTriggered': {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.$emit('resetCustomScoringUpdatedSuccess');
          this.initialCustomScoring = cloneDeep(this.currentCustomScoring)
        }
      }
    }
  },
  created() {
    this.httpConnectionOptions = httpConnectionOptions;
    this.initialCustomScoring = cloneDeep(this.currentCustomScoring),
    this.hasExistingAcceptanceParsing = !!(this.initialCustomScoring.scoreSettingsV2?.canBidPathParser && this.initialCustomScoring.scoreSettingsV2?.canBidPathParser.length);
    this.hasExistingScoreParsing = !!(this.initialCustomScoring.scoreSettingsV2?.scorePathParser && this.initialCustomScoring.scoreSettingsV2?.scorePathParser.length);
    this.hasExistingCustomTags = !!this.initialCustomScoring.scoreSettingsV2?.parseSettings;

    this.buildCustomScoringDisplay(this.customScoring);
  },
  methods: {
    ...mapActions('dynamicInboundCalls', [
      'customScoringRequest',
      'resetCustomScoringRequestStatus'
    ]),
    onCustomScoringSettingsShowContentsClick() {
      this.customScoringSettingsShowContents = !this.customScoringSettingsShowContents;
    },
    async customScoringSettingChange(event) {
      this.customScoring.state = event.value;
      this.isCustomScoringUrlValid = await this.validateCustomScoringUrl();
      this.updateCustomScoringSettings();
    },
    onCustomScoringConnectionMethodChange(event) {
      if (event === 'get') this.customScoring.scoreSettingsV2.body = '';
      this.updateCustomScoringSettings();
    },
    toggleResponse() {
      this.showRequestResponse = !this.showRequestResponse;
    },
    toggleError() {
      this.showRequestError = !this.showRequestError;
    },
    addCustomTag(tag) {
      this.customTagsSelected.push(tag);
      this.customTagsConfig.options = this.customTagsConfig.options.filter(t => t.id != tag.id).sort((a, b) => a.label.localeCompare(b.label));
      this.updateCustomScoringSettings();
    },
    removeCustomTag(payload) {
      this.customTagsSelected.splice(payload.index, 1);
      this.customTagsConfig.options.push(payload.tag);
      this.customTagsConfig.options.sort((a, b) => a.label.localeCompare(b.label));
      this.updateCustomScoringSettings();
    },
    updateScoreParsingSettings(scoreParsing) {
      this.selectedScoreParsing = scoreParsing;
      this.updateCustomScoringSettings();
    },

    buildCustomScoringDisplay(settings) {
      if (settings?.scoreSettingsV2?.body) this.customScoring.scoreSettingsV2.body = settings.scoreSettingsV2.body;
      if (settings?.scoreSettingsV2?.canBidPathParser) this.acceptanceParsing = sanitizeAcceptanceParsingForDisplay(settings.scoreSettingsV2.canBidPathParser);
      if (settings?.scoreSettingsV2?.scorePathParser) this.selectedScoreParsing = sanitizeScoreParsingForDisplay(settings.scoreSettingsV2.scorePathParser);
      if (settings?.scoreSettingsV2?.parseSettings) this.customTagsSelected = sanitizeCustomTagsForDisplay(settings.scoreSettingsV2.parseSettings);
    },

    async runRequest() {
      this.resetSettings();
      const requestPayload = {
        method: this.customScoring.scoreSettingsV2.method,
        url: this.customScoringRequestUrl,
        body: JSON.stringify(this.customScoringRequestBody),
        headers: this.customScoring.scoreSettingsV2.headers,
        contentType: 'application/json'
      }
      try {
        const response = await this.customScoringRequest(requestPayload);
        if (this.requestSuccess) { 
          this.customScoringRequestResponse = JSON.parse(response.body);
          const propertiesArray = extractProperties(this.customScoringRequestResponse);
          this.selectablePropertiesConfig.options = propertiesArray;
          this.customTagsConfig.options = propertiesArray;
        } else if (this.requestFail) {
          this.customScoringRequestError = this.customScoringRequestStatus.error;
        }
      } catch {
        this.customScoringRequestError = this.customScoringRequestStatus.error;
      }
      this.completedCustomScoringRequestUrl = this.customScoringRequestUrl;
    },
    async validateCustomScoringUrl() {
      await this.$nextTick();
      if (this.$refs.customScoringUrl) {
        const response = await this.$refs.customScoringUrl.validate();
        return response.valid;
      }
      return false;
    },
    updateCustomScoringSettings() {
      this.watchCounter++;
      const hasParsingSettings = this.acceptanceParsing[0]?.property && this.acceptanceParsing[0]?.value ? true : false;
      const customTags = sanitizeCustomTagsForRequest(this.customTagsSelected);
      const settingsObject = {
        state: hasParsingSettings ? this.customScoring.state : 'Off',
        customIntegrationType: 'score',
        scoreSettingsV2: {
          url: hasParsingSettings ? this.customScoring.scoreSettingsV2.url : '',
          method: this.customScoring.scoreSettingsV2.method,
          body: hasParsingSettings ? this.customScoring.scoreSettingsV2.body : '',
          headers: hasParsingSettings ? this.customScoring.scoreSettingsV2.headers : {},
          scorePathParser: hasParsingSettings ? sanitizeScoreParsingForRequest(this.selectedScoreParsing) : null,
          canBidPathParser: hasParsingSettings ? sanitizeAcceptanceParsingForRequest(this.acceptanceParsing) : null,
          parseSettings: hasParsingSettings ? customTags : null
        },
        scoringSettingV2Migrated: true
      }
      this.$emit('updateCustomScoringSettings', settingsObject);
    },
    isValidJson(json) {
      if (json) {
        try {
          JSON.parse(json);
          return true;
        } catch (error) {
          return false;
        }
      }
      return true;
    },
    revertChanges() {
      this.resetConstants();
      this.resetCustomScoringRequestStatus();
      this.$emit('updateCustomScoringModified', false);
      this.isCustomScoringUrlValid = false;
      this.customScoring = cloneDeep(this.initialCustomScoring),
      this.buildCustomScoringDisplay(this.initialCustomScoring);
      this.selectablePropertiesConfig.options = [];
      this.customTagsConfig.options = [];
      this.selectedMacros = [];
    },
    resetSettings() {
      this.resetConstants();
      this.$emit('updateCustomScoringModified', true);
      this.customTagsSelected = [];
      this.selectedScoreParsing = null;
      this.acceptanceParsing = [{...defaultParsingStep}];
    },
    resetConstants() {
      this.showRequestResponse = false;
      this.showRequestError = false;
      this.completedCustomScoringRequestUrl = '';
      this.customScoringRequestResponse = null;
      this.customScoringRequestError = null;
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  } 
};
</script>